declare global {
  interface Window {
    truste?: {
      cma?: {
        callApi?: (method: string, domain: string, ...args: any[]) => { consent?: string } | undefined
      }
      eu?: {
        clickListener?: () => void
      }
    }
  }
  interface Navigator {
    // Global Privacy Control signal; absent in browsers that don't implement it.
    globalPrivacyControl?: boolean
  }
}

// Checks TrustArc's API to see if the user has approved advertising cookies.
// Returns false if TrustArc hasn't loaded yet or consent was denied.
export function isAdvertisingConsentGranted(): boolean {
  return resolveAdvertisingConsent({
    diagnosticForceGrant: shouldForceGrantForDiagnostic(),
    globalPrivacyControlEnabled: isGlobalPrivacyControlEnabled(),
    trusteAdvertisingConsent: readTrusteAdvertisingConsent()
  })
}

// Decides advertising consent from the three inputs, in priority order. Pure (no globals)
// so the precedence — especially GPC overriding a TrustArc "approved" — is unit-testable.
// GPC is a hard opt-out of sale/sharing: honor it even when TrustArc still reports approval,
// because TrustArc only reads GPC at initial banner load and misses a mid-session toggle.
export function resolveAdvertisingConsent(state: {
  diagnosticForceGrant: boolean
  globalPrivacyControlEnabled: boolean
  trusteAdvertisingConsent: string | undefined
}): boolean {
  if (state.diagnosticForceGrant) return true
  if (state.globalPrivacyControlEnabled) return false
  return state.trusteAdvertisingConsent === 'approved'
}

// True when the browser is sending a Global Privacy Control signal.
function isGlobalPrivacyControlEnabled(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.globalPrivacyControl === true
}

// Reads TrustArc's advertising-bucket consent, or undefined if TrustArc hasn't loaded yet.
function readTrusteAdvertisingConsent(): string | undefined {
  if (!window.truste?.cma?.callApi) return undefined
  return window.truste.cma.callApi('getConsent', self.location.host, null, null, 'advertising')?.consent
}

// In non-prod, append `?diag=pixel-grant` (alongside `?prodConsent=true`) to bypass
// the TrustArc advertising-bucket check and force the loader to treat consent as
// granted on initial page load. Useful for local Meta Pixel smoke-tests without
// source edits. Hard-gated on ENV_NAME so it cannot activate in production even if
// the query string is set.
function shouldForceGrantForDiagnostic(): boolean {
  if (typeof window === 'undefined') return false
  if (window.ENV?.ENV_NAME === 'prod') return false
  return new URLSearchParams(window.location.search).get('diag') === 'pixel-grant'
}

// Polls for TrustArc API availability (100ms intervals, up to 50 attempts).
// Calls the callback once ready. Returns a cleanup function to cancel polling.
export function whenTrustArcReady(callback: () => void): () => void {
  if (window.truste?.cma?.callApi) {
    callback()
    return () => {}
  }
  let pollCount = 0
  const interval = setInterval(() => {
    pollCount++
    if (window.truste?.cma?.callApi) {
      clearInterval(interval)
      callback()
    } else if (pollCount >= 50) {
      clearInterval(interval)
    }
  }, 100)
  return () => clearInterval(interval)
}

// Listens for consent preference changes and invokes the callback when they occur.
// Two sources: TrustArc's preference manager (via postMessage, after a 500ms delay to
// match TrustArc's internal timing) and Global Privacy Control. Browsers don't emit an
// event when GPC is toggled in settings, so we poll navigator.globalPrivacyControl and
// fire the callback when it flips — otherwise enabling GPC mid-session has no effect.
// Returns a cleanup function that removes the listener and stops polling.
export function onConsentChange(callback: () => void): () => void {
  function handler(e: MessageEvent) {
    try {
      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
      if (data?.source === 'preference_manager' && data?.message === 'submit_preferences') {
        setTimeout(callback, 500)
      }
    } catch {
      // non-JSON postMessages are expected from other scripts
    }
  }
  window.addEventListener('message', handler)

  let lastGpc = isGlobalPrivacyControlEnabled()
  const gpcInterval = setInterval(() => {
    const currentGpc = isGlobalPrivacyControlEnabled()
    if (currentGpc !== lastGpc) {
      lastGpc = currentGpc
      callback()
    }
  }, 1000)

  return () => {
    window.removeEventListener('message', handler)
    clearInterval(gpcInterval)
  }
}
